import { useNavigate } from "@tanstack/react-router";
import { type FunctionComponent, useCallback, useMemo } from "react";
import announceIcon from "@/assets/img/icon_aimoge_alpha.png";
import type { AnnounceBannerItem } from "@/features/announce/api/announceClient";
import { useAnnounceBanners } from "@/features/announce/hooks/useAnnounceBanners";
import { useAnnouncementStatus } from "@/features/announce/hooks/useAnnouncementStatus";
import { useDesktopAnnouncementModal } from "@/features/announce/hooks/useDesktopAnnouncementModal";
import { activateAnnouncementBanner } from "@/features/announce/lib/announcementBannerTarget";
import {
  dismissAnnouncement,
  isAnnouncementDismissed,
} from "@/features/announce/lib/dismissedAnnouncements";
import {
  selectVisibleBanner,
  shouldShowAnnounceBannerSkeleton,
} from "@/features/announce/lib/selectVisibleBanner";
import {
  type AnnouncementBannerEffect,
  createAnnouncementBannerMediator,
  isAnnouncementBannerEvent,
} from "@/features/announce/mediators/announcementBannerMediator";
import {
  type AnnouncementEvent,
  isAnnouncementEvent,
} from "@/features/announce/mediators/announcementEvents";
import { useIsDesktop } from "@/shared/hooks";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

const LEVEL_CLASS: Record<AnnounceBannerItem["level"], string> = {
  emergency: "aimg-announce-lv-emergency",
  important: "aimg-announce-lv-important",
  normal: "aimg-announce-lv-normal",
};

const LEVEL_PREFIX: Record<AnnounceBannerItem["level"], string> = {
  emergency: "🚨",
  important: "⭐️",
  normal: "",
};

/**
 * カタログ上部の告知バナー。
 *
 * SPでは取得中に`AnnounceBannerSkeleton`で概算の高さを確保する。
 * PCでは一覧を先に表示し、`DesktopAnnouncementContent`がバナー挿入時の移動を補正する。
 * PCの予約高さは現revisionの取得成功後に全件dismiss済みの場合だけ解除し、0件・取得失敗では
 * 初回表示の位置ずれを避けるため維持する。SPの空状態は畳む。
 */
export const AnnounceBanner: FunctionComponent = () => {
  const bannersQuery = useAnnounceBanners();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { openAnnouncementModal } = useDesktopAnnouncementModal();
  const { hasUnreadAnnouncements } = useAnnouncementStatus();
  const mediator = useMemo(
    () =>
      createAnnouncementBannerMediator((effect: AnnouncementBannerEffect) => {
        if (effect.type === "persist-dismissal") {
          dismissAnnouncement(effect.bannerId);
          return;
        }
        activateAnnouncementBanner(effect.banner, isDesktop, {
          openDesktop: openAnnouncementModal,
          openMobileDetail: (announcementId: number): void => {
            void navigate({
              to: "/announcements/$announcementId",
              params: { announcementId: String(announcementId) },
            });
          },
        });
      }),
    [isDesktop, navigate, openAnnouncementModal],
  );
  useHierarchyLifetime(mediator);
  const state = useMediatorSnapshot(mediator);
  const handler = useCallback(
    (event: { readonly type: string }): "handled" | "unhandled" => {
      if (!isAnnouncementEvent(event)) return "unhandled";
      if (!isAnnouncementBannerEvent(event)) return "unhandled";
      const result = mediator.dispatch(event);
      return result.status === "handled" ? "handled" : "unhandled";
    },
    [mediator],
  );
  const node = useComponentNode(handler);

  if (bannersQuery.data === undefined) {
    if (isDesktop) return null;
    return shouldShowAnnounceBannerSkeleton(
      bannersQuery.data,
      bannersQuery.isError,
    ) ? (
      <AnnounceBannerSkeleton />
    ) : null;
  }

  const visible = selectVisibleBanner(
    bannersQuery.data,
    (id) => state.dismissedIds.has(id) || isAnnouncementDismissed(id),
  );
  // 別revisionから引き継いだinitialDataは成功状態でもdataUpdatedAtが0。
  // 現revisionの応答を待つ間は、旧データのdismiss状態で予約高さを解除しない。
  if (!visible)
    return isDesktop &&
      bannersQuery.isSuccess &&
      bannersQuery.dataUpdatedAt > 0 &&
      bannersQuery.data.length > 0 ? (
      <AnnounceBannerEmpty />
    ) : null;

  return (
    <ComponentScope node={node}>
      <AnnounceBannerView
        banner={visible}
        showNewBadge={hasUnreadAnnouncements}
        onEvent={(event): void => {
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
};

/** 全件dismiss済みの場合にPCカタログの予約高さを解除する描画マーカー。 */
const AnnounceBannerEmpty: FunctionComponent = () => (
  <span className="aimg-announce-empty" hidden />
);

/**
 * 取得中のプレースホルダ。実際のバナー(`<aside>`)とほぼ同じ外枠
 * (border-b・padding)を持たせ、取得完了時にバナーが表示される場合の
 * 高さの変動を抑える。タイトル1行分の概算高さを確保する。装飾目的のみで
 * 実際の情報を持たないため`aria-hidden`でスクリーンリーダーから隠す。
 */
const AnnounceBannerSkeleton: FunctionComponent = () => (
  <span className="aimg-announce-skeleton" aria-hidden="true" />
);

interface AnnounceBannerViewProps {
  banner: AnnounceBannerItem;
  onEvent: (event: AnnouncementEvent) => void;
  showNewBadge?: boolean;
}

/**
 * 状態を持たない描画専用View。`AnnounceBanner`から分離し、
 * データ取得・dismiss記憶なしでmarkupだけをテストできるようにする。
 */
export const AnnounceBannerView: FunctionComponent<AnnounceBannerViewProps> = ({
  banner,
  onEvent,
  showNewBadge = false,
}: AnnounceBannerViewProps) => {
  return (
    <aside aria-label="お知らせ" className="aimg-announce-root">
      <button
        type="button"
        onClick={(): void =>
          onEvent({ type: "announcement/banner-opened", banner })
        }
        className="aimg-announce-link"
        aria-label={`お知らせ: ${banner.title}`}
      >
        <span className="aimg-announce-icon-wrap">
          <img
            className="aimg-announce-icon"
            src={announceIcon}
            alt=""
            width={32}
            height={32}
          />
          {showNewBadge && <strong className="aimg-announce-badge">new</strong>}
        </span>
        <strong
          className={`aimg-announce-title ${LEVEL_CLASS[banner.level]}`}
          aria-live="polite"
        >
          {LEVEL_PREFIX[banner.level]}
          {banner.title}
        </strong>
      </button>
      <button
        type="button"
        onClick={(): void =>
          onEvent({
            type: "announcement/banner-dismissed",
            bannerId: banner.id,
          })
        }
        aria-label="お知らせを閉じる"
        className="aimg-announce-close"
      >
        ✕
      </button>
    </aside>
  );
};
